import { type RefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type Query, useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Spinner } from "frontend-components";

import { taskListWithTasksKeys, taskListWithTasksOptions, userKeys } from "@dashboard/api/queryApi";
import { createTaskAbility, TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { ListViewBody } from "@dashboard/components/common/ListModal/ListViewBody";
import { PrintModeContext } from "@dashboard/contexts/printModeContext";
import { taskCommentKeys } from "@dashboard/hooks/useTaskCommentQuery";
import { taskImageKeys } from "@dashboard/hooks/useTaskImageQuery";

import "./print.css";

// No rules: every <Can> is false, so add comment/image and comment menus are hidden and statuses are read-only.
const readOnlyAbility = createTaskAbility();

// onDone is called after the print dialog closes or the error is dismissed.
export function ChecklistPrintPage({ listId, onDone }: { listId: number; onDone: () => void }) {
    const { t } = useTranslation();
    const { data: list, isLoading, error } = useQuery(taskListWithTasksOptions(listId));

    const contentRef = useRef<HTMLDivElement>(null);

    usePrintWhenReady(contentRef, list?.title, onDone);

    const errorMessage =
        !isLoading && (error || !list)
            ? error?.message || `${t("common.couldNotFindListWithId")}: ${listId}`
            : undefined;

    // The page is rendered off-screen, so progress and errors are shown in an overlay.
    const overlay = <PrintStatusOverlay errorMessage={errorMessage} onClose={onDone} />;

    if (isLoading) {
        return (
            <div className="checklist-print flex items-center justify-center py-10">
                {overlay}
                <Spinner className="size-6" />
            </div>
        );
    }

    if (error || !list) {
        return (
            <div className="checklist-print flex flex-col gap-3 p-6">
                {overlay}
                <h2 className="text-h3 font-medium">{t("common.listNotFound")}</h2>
                <p className="text-text-secondary">{errorMessage}</p>
            </div>
        );
    }

    return (
        <TaskAbilityContext.Provider value={readOnlyAbility}>
            <PrintModeContext.Provider value={true}>
                {overlay}
                <div
                    ref={contentRef}
                    className="checklist-print bg-compendia-card text-compendia-foreground p-6 flex flex-col"
                >
                    <h1 className="-mx-6 px-6 pb-4 border-b border-compendia-border text-lg font-medium leading-tight">
                        {list.title}
                    </h1>
                    <ListViewBody list={list} />
                </div>
            </PrintModeContext.Provider>
        </TaskAbilityContext.Provider>
    );
}

function PrintStatusOverlay({
    errorMessage,
    onClose,
}: {
    errorMessage?: string;
    onClose: () => void;
}) {
    const { t } = useTranslation();

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 pointer-events-auto print:hidden"
            // Keeps presses here from counting as outside presses that close the checklist dialog.
            onPointerDown={(event) => event.stopPropagation()}
        >
            <div
                role={errorMessage ? "alert" : "status"}
                className="bg-compendia-card text-compendia-foreground rounded-[3px] p-6 flex flex-col items-center gap-3 max-w-sm"
            >
                {errorMessage ? (
                    <>
                        <h2 className="text-h3 font-medium">{t("common.listNotFound")}</h2>
                        <p className="text-text-secondary text-center">{errorMessage}</p>
                        <Button variant="outline" onClick={onClose}>
                            {t("common.close")}
                        </Button>
                    </>
                ) : (
                    <>
                        <Spinner className="size-6" />
                        <p className="text-sm">{t("list.preparingPdf")}</p>
                    </>
                )}
            </div>
        </div>
    );
}

// Only the checklist's own data, so unrelated dashboard queries (e.g. retrying failures) don't delay printing.
const checklistQueryRoots = new Set<unknown>([
    taskListWithTasksKeys.all[0],
    taskCommentKeys.all[0],
    taskImageKeys.all[0],
    userKeys.all[0],
]);
const isChecklistQuery = (query: Query) => checklistQueryRoots.has(query.queryKey[0]);

// Opens the print dialog once, after all queries (comments, images) and images/fonts have loaded.
// The document title becomes the default PDF file name.
function usePrintWhenReady(
    contentRef: RefObject<HTMLDivElement | null>,
    title: string | undefined,
    onDone: () => void,
) {
    const queryClient = useQueryClient();
    const isFetching = useIsFetching({ predicate: isChecklistQuery });
    const printed = useRef(false);
    const restore = useRef<() => void>(undefined);

    // Also on unmount: the next export unmounts this page even if no print event fired.
    useEffect(() => () => restore.current?.(), []);

    useEffect(() => {
        const content = contentRef.current;
        if (!content || title === undefined || isFetching > 0 || printed.current) return;

        let cancelled = false;
        const pendingImages = Array.from(content.querySelectorAll("img"))
            .filter((img) => !img.complete)
            .map(
                (img) =>
                    new Promise((resolve) => {
                        img.addEventListener("load", resolve, { once: true });
                        img.addEventListener("error", resolve, { once: true });
                    }),
            );

        void Promise.all([...pendingImages, document.fonts.ready]).then(() => {
            // Child queries may have started after this render; the next isFetching change re-runs this.
            if (
                cancelled ||
                printed.current ||
                queryClient.isFetching({ predicate: isChecklistQuery }) > 0
            )
                return;
            printed.current = true;
            const previousTitle = document.title;
            // afterprint isn't always fired in Safari / iOS, so the end of print media is a second signal.
            const printMedia = window.matchMedia("print");
            const onPrintMediaChange = (event: MediaQueryListEvent) => {
                if (!event.matches) finish();
            };
            const reset = () => {
                restore.current = undefined;
                window.removeEventListener("afterprint", finish);
                printMedia.removeEventListener("change", onPrintMediaChange);
                document.title = previousTitle;
            };
            const finish = () => {
                reset();
                onDone();
            };
            restore.current = reset;
            window.addEventListener("afterprint", finish);
            printMedia.addEventListener("change", onPrintMediaChange);
            document.title = title;
            window.print();
        });

        return () => {
            cancelled = true;
        };
    }, [contentRef, title, isFetching, queryClient, onDone]);
}
